import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { router } from './App';
import { initNative } from '@/lib/native';
import './index.css';

// Sentry init. No-op when DSN is not provided so the app stays safe in
// dev/local builds. Replays + tracing are scaffolded with conservative sample
// rates appropriate for client SPAs.
const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
}

// Global fallback for broken images (e.g. product images whose files aren't on
// the server yet). Image 'error' events don't bubble, so listen in the capture
// phase at the document level. The data-img-fallback guard prevents an infinite
// loop if the placeholder itself ever fails to load.
document.addEventListener(
  'error',
  (e) => {
    const el = e.target as HTMLElement | null;
    if (el && el.tagName === 'IMG') {
      const img = el as HTMLImageElement;
      if (!img.dataset.imgFallback) {
        img.dataset.imgFallback = '1';
        img.src = '/images/placeholder.svg';
      }
    }
  },
  true,
);

// HelmetProvider wraps RouterProvider so route-driven <Helmet> updates always
// have a context. Data router (createBrowserRouter) is required for useBlocker
// (used by the design editor's unsaved-changes navigation guard).
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  </React.StrictMode>,
);

// Native shell init (Capacitor): hide splash, theme status bar, wire the
// Android back button + deep links. No-op on the web build.
void initNative({ onDeepLink: (path) => router.navigate(path) });
